import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import {
  Box,
  ControlShell,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  SelectTrigger,
  Text,
  Toggle,
  WidgetControlsProvider,
  type WidgetControlProps,
  type WidgetControls,
  type WidgetSettingOption,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { DateRangePresetPicker } from "#app/components/filters/date-range-preset-picker";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import { countActiveFilters } from "#app/lib/filters/build-where";
import type { FilterState } from "#app/lib/filters/types";
import { useCustomFieldsList } from "#app/lib/hooks/use-custom-fields-list";
import { useFilters } from "#app/lib/hooks/use-filters";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { getPresetShortLabel } from "#app/lib/utils/date-presets";
// Direct module paths into the dashboards feature (never its barrel — the
// barrel leads back to the registry, which this file's consumers sit beside).
import { parseWidgetFilters, type WidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";

/**
 * The app's binding of the widget kit's control-registry seam. It supplies the
 * domain-specific settings pieces the generic {@link SettingsFields} renderer
 * dispatches to: custom controls (`fields`, `issueFilters`, `source`) and the
 * `customFields` / `workflows` option sources (for `dynamicOptions` selects).
 *
 * Mount it inside `<WidgetRegistryProvider>` in the workspace layout. Adding a new
 * domain field kind is a new entry here — the kit never changes.
 */
export function AppWidgetControls({ children }: { children: ReactNode }) {
  const value = useMemo<WidgetControls>(
    () => ({
      controls: {
        fields: FieldsControl,
        issueFilters: IssueFiltersControl,
        source: SourceControl,
      },
      optionSources: {
        customFields: useCustomFieldsAsOptions,
        workflows: useWorkflowsAsOptions,
      },
    }),
    [],
  );
  return <WidgetControlsProvider value={value}>{children}</WidgetControlsProvider>;
}

/** `kind: "source"` — the read-only "where does this widget's data come from"
 * row: the dashboard's pinned entity as a chip, or "All data" off-scope. */
function SourceControl() {
  const scope = useDashboardScope();
  const workspace = useWorkspaceData();
  const scopedEntity = scope?.entity;
  const entity = useMemo(() => {
    if (scopedEntity?.type === "workflow") {
      const workflow = workspace.workflowMap.get(scopedEntity.id);
      return workflow ? { kind: "workflow" as const, id: scopedEntity.id, workflow } : null;
    }
    if (scopedEntity?.type === "team") {
      const team = workspace.teamMap.get(scopedEntity.id);
      return team ? { kind: "team" as const, id: scopedEntity.id, team } : null;
    }
    return null;
  }, [scopedEntity?.id, scopedEntity?.type, workspace.teamMap, workspace.workflowMap]);

  return (
    <Box
      width="100%"
      height={32}
      surface="card"
      rounded="md"
      border
      paddingX="sm"
      direction="row"
      align="center"
      justify="start"
    >
      {entity ? (
        <EntityTag entity={entity} display="full" interactive={false} />
      ) : (
        <Text size="xs" variant="muted">
          All data
        </Text>
      )}
    </Box>
  );
}

/** The advanced builder opens showing one empty row, not a bare "Add filter"
 * button — the same seed the query resets to on clear. */
const SEEDED_EMPTY_QUERY: FilterState = {
  logic: "AND",
  blocks: [{ logic: "AND", filters: [{ field: "", operator: "eq", value: null }] }],
};

/**
 * `kind: "issueFilters"` — a widget's own Filters row, mirroring the dashboard
 * toolbar's chip language: an advanced query-builder popover, a date-range
 * preset chip, and a "Show archived" toggle chip. The stored value is ONE
 * `WidgetFilters` blob under the field's key (see `widget-filters.ts`); each
 * chip commits a partial patch of it. The widget's data hook and the Data
 * drill-down both fold the same blob via `resolveWidgetFilters`.
 */
function IssueFiltersControl({ field, value, disabled, onCommit }: WidgetControlProps) {
  const filters = useMemo(() => parseWidgetFilters(value), [value]);

  const commitPatch = useCallback(
    (patch: Partial<WidgetFilters>) => {
      const current = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
      onCommit(field.key, { ...current, ...patch });
    },
    [field.key, value, onCommit],
  );

  // The builder state is local (seeded once from the persisted query — the
  // settings editors remount per instance, so no reset effect); the commit
  // effect below writes it back whenever it meaningfully changes.
  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: (filters.query as FilterState | null | undefined) ?? SEEDED_EMPTY_QUERY,
  });

  // Persist the builder state per change: active filters store the whole state
  // (so reopening restores the built query), zero active filters store null
  // (half-built rows aren't worth sharing workspace-wide). The serialized
  // guard breaks the loop the round-trip would otherwise cause (commit →
  // `value` changes → `commitPatch` identity changes → effect re-runs).
  const lastCommittedQuery = useRef(JSON.stringify(filters.query ?? null));
  useEffect(() => {
    const next = countActiveFilters(query.advancedState) > 0 ? query.advancedState : null;
    const key = JSON.stringify(next);
    if (key === lastCommittedQuery.current) return;
    lastCommittedQuery.current = key;
    commitPatch({ query: next });
  }, [query.advancedState, commitPatch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  const dateRange = filters.dateRange ?? null;
  const showArchived = filters.showArchived === true;

  return (
    <Box direction="row" gap="sm" align="center" wrap>
      <Popover>
        <PopoverTrigger
          render={
            <FilterToggle
              size="xs"
              icon="filter"
              count={query.activeFilterCount}
              disabled={disabled}
              onClear={clearQuery}
            />
          }
        />
        {/* No portalHost: Base UI portals to body and stacks nested layers
          * natively (`--nested-dialogs`), so this popover opened from inside
          * the settings dialog needs no shared host. */}
        <PopoverPopup
          align="start"
          padding="none"
          className="w-[360px]"
        >
          <Box direction="column" maxHeight={440}>
            <ScrollArea>
              <AdvancedFilters config={issueFilterConfig} filters={query} />
            </ScrollArea>
          </Box>
        </PopoverPopup>
      </Popover>
      <DateRangePresetPicker
        value={dateRange}
        onChange={(next) => commitPatch({ dateRange: next })}
        align="start"
      >
        <SelectTrigger chevron={false}
          render={
            <FilterToggle
              size="xs"
              icon="calendar"
              label={dateRange ? getPresetShortLabel(dateRange.key) : "Following page"}
              selected={!!dateRange}
              disabled={disabled}
              onClear={() => commitPatch({ dateRange: null })}
            />
          }
        />
      </DateRangePresetPicker>
      {/* Self-contained labeled toggle: icon + text stay visible in both
          states, so the footprint never jumps. */}
      <Toggle
        size="xs"
        variant="outline"
        disabled={disabled}
        pressed={showArchived}
        onPressedChange={(next) => commitPatch({ showArchived: next })}
      >
        <Icon name="archive" size="sm" />
        <Text size="xs" weight="medium" truncate={1}>
          Show archived
        </Text>
      </Toggle>
    </Box>
  );
}

/**
 * `kind: "fields"` — a workspace custom-field multiselect rendered as toggle chips.
 * The stored value is the selected `customFieldId[]`.
 */
function FieldsControl({ field, value, disabled, onCommit }: WidgetControlProps) {
  const options = useCustomFieldsAsOptions();
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const toggle = (id: string) =>
    onCommit(
      field.key,
      selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id],
    );
  return (
    <ControlShell label={field.label} description={field.description}>
      {options.length === 0 ? (
        <Text size="sm" variant="muted">
          No custom fields in this workspace.
        </Text>
      ) : (
        <Box direction="row" gap="xs" wrap>
          {options.map((opt) => {
            const on = selected.includes(opt.value);
            return (
              <Toggle
                key={opt.value}
                variant="outline"
                size="sm"
                pressed={on}
                disabled={disabled}
                onPressedChange={() => toggle(opt.value)}
              >
                {on ? <Icon name="check" size="xs" /> : null}
                <Text>{opt.label}</Text>
              </Toggle>
            );
          })}
        </Box>
      )}
    </ControlShell>
  );
}

/** Workspace custom fields as select options. */
function useCustomFieldsAsOptions(): WidgetSettingOption[] {
  const { data } = useCustomFieldsList(false);
  return useMemo(() => {
    const nodes = data?.customFields?.edges?.map((e) => e.node) ?? [];
    return nodes.map((cf) => ({ value: cf.customFieldId, label: cf.name }));
  }, [data]);
}

/** Workspace workflows as select options (the already-loaded workspace data — no
 *  extra query). */
function useWorkflowsAsOptions(): WidgetSettingOption[] {
  const { workflows } = useWorkspaceData();
  return useMemo(
    () => workflows.map((w) => ({ value: w.workflowId, label: w.name ?? "Untitled workflow" })),
    [workflows],
  );
}
